import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Euro, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

/** Prezzi e unita per confezione dei farmaci, usati per la spesa stimata del cruscotto. */
export const PrezziFarmaciDialog = ({ open, onOpenChange, farmaciSuggeriti = [], onSalvato }) => {
  const [righe, setRighe] = useState([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/farmaci-confezioni')
      .then(r => {
        const esistenti = r.data.map(c => ({
          farmaco: c.farmaco, dosaggio: c.dosaggio || '',
          unita: String(c.unita_per_confezione || ''), prezzo: String(c.prezzo_confezione || ''),
        }));
        const nomi = new Set(esistenti.map(e => e.farmaco.toUpperCase()));
        const nuovi = farmaciSuggeriti
          .filter(f => !nomi.has((f.farmaco || '').toUpperCase()))
          .map(f => ({ farmaco: f.farmaco, dosaggio: '', unita: '', prezzo: '' }));
        setRighe([...nuovi, ...esistenti]);
      })
      .catch(() => toast.error('Errore nel caricamento dei prezzi'))
      .finally(() => setLoading(false));
  }, [open, farmaciSuggeriti]);

  const aggiorna = (i, campo, valore) =>
    setRighe(r => r.map((x, idx) => (idx === i ? { ...x, [campo]: valore } : x)));

  const salva = async (i) => {
    const r = righe[i];
    const unita = parseInt(r.unita, 10);
    const prezzo = parseFloat((r.prezzo || '').replace(',', '.'));
    if (!unita || unita <= 0) return toast.error('Indica quante unita contiene la confezione');
    setSalvando(i);
    try {
      await api.put('/farmaci-confezioni', {
        farmaco: r.farmaco, dosaggio: r.dosaggio,
        unita_per_confezione: unita, prezzo_confezione: isNaN(prezzo) ? 0 : prezzo,
      });
      toast.success(`${r.farmaco} aggiornato`);
      onSalvato?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel salvataggio'); }
    setSalvando(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="prezzi-farmaci-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Euro className="w-4 h-4 text-brand" />Prezzi e confezioni dei farmaci
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-xs text-slate-500">
          Indica quante unita contiene la confezione e il prezzo pagato: la spesa del cruscotto usa questi valori.
        </DialogDescription>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-brand" /></div>
        ) : (
          <div className="space-y-2">
            {righe.length === 0 && <p className="text-xs text-slate-400 py-6 text-center">Nessun farmaco da valorizzare</p>}
            {righe.map((r, i) => (
              <div key={`${r.farmaco}-${i}`} className="grid grid-cols-12 gap-2 items-end rounded-lg border border-slate-200 p-2">
                <div className="col-span-6">
                  <Label className="text-[10px] text-slate-500">Farmaco</Label>
                  <p className="text-xs font-medium text-slate-800 truncate" title={r.farmaco}>{r.farmaco}</p>
                </div>
                <div className="col-span-3">
                  <Label className="text-[10px] text-slate-500">Unita/conf.</Label>
                  <Input className="h-8 text-xs" type="number" min="1" value={r.unita}
                    data-testid={`prezzo-unita-${i}`}
                    onChange={e => aggiorna(i, 'unita', e.target.value)} placeholder="30" />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] text-slate-500">Prezzo EUR</Label>
                  <Input className="h-8 text-xs" value={r.prezzo}
                    data-testid={`prezzo-valore-${i}`}
                    onChange={e => aggiorna(i, 'prezzo', e.target.value)} placeholder="4,50" />
                </div>
                <Button size="sm" variant="outline" className="col-span-1 h-8 px-0"
                  data-testid={`prezzo-salva-${i}`} disabled={salvando === i} onClick={() => salva(i)}>
                  {salvando === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PrezziFarmaciDialog;
